import React, { useCallback } from 'react';
import { ValidOption, ValidOptionalInput } from './types';
import ValidInputError from './ValidInputError';
import ValidErrorMarker from './ValidErrorMarker';
import { TblFltOption } from '../../table/table.types';

// TYPE
export type ValidPropRadioBool = {
    key: string;
    lbl: string;
    val: boolean | undefined;
    def: boolean | undefined;
    options: ValidOption[];
    err: string;
};

function defaultBoolOptions(): TblFltOption[] {
    return [
        { lbl: 'No', val: 'false' },
        { lbl: 'Yes', val: 'true' },
    ];
}

// VIEW MODEL
export function validPropRadioBool(
    key: string,
    lbl?: string,
    val?: any,
    def?: boolean,
    options?: ValidOption[],
): ValidPropRadioBool {
    return {
        key: key,
        lbl: !!lbl ? lbl : '',
        val: typeof val === 'undefined' && typeof def !== 'undefined' ? def : val,
        def: def ?? undefined,
        options: Array.isArray(options) ? options : defaultBoolOptions(),
        err: '',
    };
}

// INPUT COMPONENTS
export type InputRadioProps = ValidOptionalInput & {
    prop: ValidPropRadioBool;
    group?: string;
    disabled?: boolean;
    onChange: (prop: string, val: string | boolean) => void;
    fullWidth?: boolean;
};

const valSave = (val: string): boolean => (val === 'true' ? true : false);

const valRender = (val: any): string => {
    switch (typeof val) {
        case 'string':
            return val;
        case 'boolean':
            // radio html options are always text
            return !!val ? 'true' : 'false';
        default:
            return '';
    }
};

const ValidInputRadioBool = ({
    prop,
    group,
    disabled,
    onChange,
    checked,
    onCheckboxChange,
    fullWidth,
}: InputRadioProps): JSX.Element => {
    const radioGroup = 'radioGrp' + group ? group + prop.key : prop.key;
    const propVal = valRender(prop.val);
    const showCheckbox = !!onCheckboxChange && checked !== undefined;

    const handleChange = useCallback(
        (e): void => {
            onChange(prop.key, valSave(e.target.value));
        },
        [onChange, prop.key],
    );

    const handleCheckboxChange = useCallback((): void => {
        if (showCheckbox) {
            onCheckboxChange(!checked);
        }
    }, [checked, onCheckboxChange, showCheckbox]);

    return (
        <div className="form-field input-radio">
            <div className={`input-wrapper ${fullWidth ? 'full-width' : ''}`}>
                {prop.lbl ? (
                    <label
                        className="valid-input-label"
                        onClick={showCheckbox ? handleCheckboxChange : undefined}
                    >
                        {showCheckbox ? (
                            <span className={`icon-glyph ${checked ? 'icon-checked' : 'icon-unchecked'}`} />
                        ) : null}
                        {prop.lbl}
                    </label>
                ) : null}
                <ul className="valid-input">
                    {prop.options.map((opt, idx) => {
                        return (
                            <li key={idx}>
                                <label>
                                    <input
                                        type="radio"
                                        name={radioGroup}
                                        value={opt.val}
                                        checked={opt.val == propVal}
                                        disabled={disabled}
                                        onChange={handleChange}
                                    />{' '}
                                    {opt.lbl}
                                </label>
                            </li>
                        );
                    })}
                </ul>
                <ValidErrorMarker showMarker={!!prop.err} />
            </div>
            <ValidInputError
                err={prop.err}
                showMsg
            />
        </div>
    );
};

export default ValidInputRadioBool;
